/**
 * Message HTML de « Prouver » (Franck, 07/10/2026) : editeur visuel, et un
 * bouton « Code HTML » pour coller ou retoucher le code. Edition native du
 * navigateur (contentEditable) : aucune dependance ajoutee.
 *
 * Le code colle est NETTOYE avant d'etre affiche dans la page : scripts,
 * attributs on…, liens javascript: et blocs <style> retires - un <style>
 * s'appliquerait a toute l'application. Les styles doivent donc etre en
 * ligne (attribut style), comme dans la plupart des e-mails. Par l'API, le
 * HTML du client part tel quel ; ce nettoyage ne concerne que cet ecran.
 */
import { useEffect, useRef, useState } from "react";

const INTERDITS = "script,style,iframe,object,embed,link,meta,base,form,input,button,textarea,select";

export function nettoyer(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll(INTERDITS).forEach((n) => n.remove());
  doc.querySelectorAll("*").forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const nom = attr.name.toLowerCase();
      const valeur = attr.value.trim().toLowerCase();
      if (nom.startsWith("on") || ((nom === "href" || nom === "src") && valeur.startsWith("javascript:"))) {
        el.removeAttribute(attr.name);
      }
    }
  });
  return doc.body.innerHTML;
}

const OUTILS: { commande: string; libelle: string; titre: string }[] = [
  { commande: "bold", libelle: "G", titre: "Gras" },
  { commande: "italic", libelle: "I", titre: "Italique" },
  { commande: "underline", libelle: "S", titre: "Souligné" },
  { commande: "insertUnorderedList", libelle: "• Liste", titre: "Liste à puces" },
];

export function EditeurHtml({ id, valeur, onChange }: { id: string; valeur: string; onChange: (html: string) => void }) {
  const zone = useRef<HTMLDivElement>(null);
  const [code, setCode] = useState(false);

  // Le contenu affiche n'est reecrit que lorsqu'il vient d'ailleurs (vidage
  // apres envoi, retour du mode code) : le reecrire a chaque frappe ferait
  // sauter le curseur.
  useEffect(() => {
    if (!code && zone.current && zone.current.innerHTML !== valeur) zone.current.innerHTML = valeur;
  }, [code, valeur]);

  const appliquer = (commande: string, argument?: string) => {
    zone.current?.focus();
    document.execCommand(commande, false, argument);
    onChange(zone.current?.innerHTML ?? "");
  };

  return (
    <div className="stack-serre">
      <div className="actions" style={{ margin: 0, flexWrap: "wrap" }}>
        {!code && OUTILS.map((o) => (
          <button key={o.commande} type="button" className="btn btn--secondary" title={o.titre}
                  onMouseDown={(e) => e.preventDefault()} onClick={() => appliquer(o.commande)}>
            {o.libelle}
          </button>
        ))}
        {!code && (
          <>
            <button type="button" className="btn btn--secondary" onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { const url = window.prompt("Adresse du lien (https://…)"); if (url) appliquer("createLink", url); }}>
              Lien
            </button>
            <button type="button" className="btn btn--secondary" title="Image hébergée (logo…), par son adresse"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { const url = window.prompt("Adresse de l'image (https://…)"); if (url) appliquer("insertImage", url); }}>
              Image
            </button>
          </>
        )}
        <button type="button" className={code ? "btn btn--primary" : "btn btn--secondary"}
                onClick={() => { if (code) onChange(nettoyer(valeur)); setCode(!code); }}>
          {code ? "Revenir à l'éditeur" : "Code HTML"}
        </button>
      </div>
      {code ? (
        <textarea id={id} className="input" rows={10} value={valeur} spellCheck={false}
                  style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                  onChange={(e) => onChange(e.target.value)} />
      ) : (
        <div id={id} ref={zone} className="input" contentEditable role="textbox" aria-multiline="true"
             style={{ minHeight: "9rem", overflow: "auto" }}
             onInput={() => onChange(zone.current?.innerHTML ?? "")} />
      )}
      <p className="muted" style={{ margin: 0 }}>
        Mise en forme en ligne uniquement (attribut style) ; les images sont insérées par leur adresse.
      </p>
    </div>
  );
}
